import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { getCompanyConversations } from '../../../../domain/use_cases/conversations/getCompanyConversations';
import { markConversationRead } from '../../../../domain/use_cases/conversations/markConversationRead';
import { CompanyConversation } from '../../../../domain/models/Conversation';
import { CompanyContact, ContactSubscription, ContactTag } from '../../../../domain/models/Contact';
import { buildSubscriptionOptions } from '../../contacts/types/ContactDisplay';
import {
  ConversationDisplay,
  ConversationsFilters,
  CONVERSATIONS_PERIOD_OPTIONS,
  CONVERSATIONS_READ_STATE_OPTIONS,
  DEFAULT_CONVERSATIONS_FILTERS,
  buildConversationDisplay,
  buildEmptyConversation,
  countActiveFilters,
  countUnreadByTab,
  filterConversations,
  getConversationKey,
  getPendingCursors,
  mergeConversationPages,
  sortConversations,
} from '../types/ConversationDisplay';

const ALL_SUBSCRIPTIONS_LABEL = "Toute l'entreprise";

type ConversationContact = Omit<CompanyContact, 'subscription_id'>;

// A conversation opened from a link (the "Conversation" button of a contact), shown even before its page is loaded
export interface ConversationTarget {
  subscriptionId: string;
  phone: string;
}

const useConversationsPageController = (initialTarget: ConversationTarget | null = null) => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany, users } = useContext(CompanyContext);

  const [conversations, setConversations] = useState<CompanyConversation[]>([]);
  const [contacts, setContacts] = useState<ConversationContact[]>([]);
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [subscriptions, setSubscriptions] = useState<ContactSubscription[]>([]);
  const [nextCursors, setNextCursors] = useState<Record<string, number> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filters, setFilters] = useState<ConversationsFilters>(DEFAULT_CONVERSATIONS_FILTERS);
  const [selectedTarget, setSelectedTarget] = useState<ConversationTarget | null>(initialTarget);
  // Filters of a failed next page: the list stops asking it until the filters change or a refresh
  const [loadMoreFailedFilters, setLoadMoreFailedFilters] = useState<ConversationsFilters | null>(null);

  // Bumped on every fetch: a page landing after the company changed belongs to a stale fetch
  const fetchIdRef = useRef(0);

  const stopFetching = useCallback(() => {
    fetchIdRef.current += 1;
  }, []);

  const companyId = activeCompany?.id;

  const fetchConversations = useCallback(async () => {
    if (!companyId || !authentication) return;

    const fetchId = ++fetchIdRef.current;
    const isStale = () => fetchId !== fetchIdRef.current;

    setIsLoading(true);
    setIsLoadingMore(false);
    setLoadMoreFailedFilters(null);
    try {
      const response = await getCompanyConversations({ company_id: companyId }, authentication);
      if (isStale()) return;
      setConversations(response.conversations);
      setContacts(response.contacts);
      setTags(response.tags ?? []);
      setSubscriptions(response.subscriptions ?? []);
      setNextCursors(response.next_cursors);
    } catch (e) {
      if (isStale()) return;
      message.error(e instanceof Error ? e.message : 'Erreur lors du chargement des conversations');
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, [companyId, authentication]);

  useEffect(() => {
    // Synchro avec une source distante : le setState synchrone est le passage en chargement qui precede l'appel.
    // eslint-disable-next-line react/set-state-in-effect
    fetchConversations();
    return stopFetching;
  }, [fetchConversations, stopFetching]);

  // Only the lines the filters can show are paged: the others would load their whole history for nothing
  const pendingCursors = useMemo(() => getPendingCursors(nextCursors, filters), [nextCursors, filters]);

  // Next page, asked when the list is scrolled to its end or does not fill its height
  const loadMore = useCallback(async () => {
    if (!companyId || !authentication || !pendingCursors || isLoading || isLoadingMore) return;

    const fetchId = fetchIdRef.current;
    const isStale = () => fetchId !== fetchIdRef.current;

    setIsLoadingMore(true);
    try {
      const response = await getCompanyConversations({ company_id: companyId, cursors: pendingCursors }, authentication);
      if (isStale()) return;
      setConversations((previous) => mergeConversationPages(previous, response.conversations));
      setContacts((previous) => {
        const loadedIds = new Set(previous.map((contact) => contact.id));
        return [...previous, ...response.contacts.filter((contact) => !loadedIds.has(contact.id))];
      });
      // The lines left out of this page keep their cursor
      setNextCursors((previous) => {
        const cursors = { ...previous, ...response.next_cursors };
        Object.keys(pendingCursors).forEach((subscriptionId) => {
          if (!response.next_cursors?.[subscriptionId]) delete cursors[subscriptionId];
        });
        return Object.keys(cursors).length > 0 ? cursors : null;
      });
    } catch (e) {
      if (isStale()) return;
      setLoadMoreFailedFilters(filters);
      message.error(e instanceof Error ? e.message : 'Erreur lors du chargement des conversations');
    } finally {
      if (!isStale()) setIsLoadingMore(false);
    }
  }, [companyId, authentication, pendingCursors, isLoading, isLoadingMore, filters]);

  const userNameBySubscription = useMemo(() => {
    const names = new Map<string, string>();
    users.forEach((user) => {
      const userName = `${user.firstname} ${user.lastname}`.trim() || user.email || '';
      Object.keys(user.idSubscription || {}).forEach((subscriptionId) => names.set(subscriptionId, userName));
    });
    return names;
  }, [users]);

  const tagsById = useMemo(() => new Map(tags.map((tag) => [tag.id, tag])), [tags]);
  const contactsById = useMemo(() => new Map(contacts.map((contact) => [contact.id, contact])), [contacts]);

  const displayedConversations = useMemo(
    () =>
      conversations.map((conversation) =>
        buildConversationDisplay(conversation, contactsById, tagsById, subscriptions, userNameBySubscription),
      ),
    [conversations, contactsById, tagsById, subscriptions, userNameBySubscription],
  );

  // The counters of the tabs ignore the tab itself, they follow the other filters
  const unreadByTab = useMemo(
    () => countUnreadByTab(filterConversations(displayedConversations, { ...filters, tab: 'all' })),
    [displayedConversations, filters],
  );

  const filteredConversations = useMemo(
    () => sortConversations(filterConversations(displayedConversations, filters), filters.sortBy),
    [displayedConversations, filters],
  );

  const subscriptionOptions = useMemo(
    () => buildSubscriptionOptions(subscriptions, userNameBySubscription, ALL_SUBSCRIPTIONS_LABEL),
    [subscriptions, userNameBySubscription],
  );

  const updateFilter = <K extends keyof ConversationsFilters>(key: K) => (value: ConversationsFilters[K]) =>
    setFilters((previous) => ({ ...previous, [key]: value }));

  // One line under the panel: scope · period · read state
  const filtersSummary = useMemo(() => {
    const subscription = subscriptions.find((item) => item.id === filters.subscriptionId);
    const scopeLabel = subscription?.is_own
      ? 'Vous'
      : subscriptionOptions.find((option) => option.value === (filters.subscriptionId ?? ''))?.label;
    return [
      scopeLabel,
      CONVERSATIONS_PERIOD_OPTIONS.find((option) => option.value === filters.period)?.label,
      CONVERSATIONS_READ_STATE_OPTIONS.find((option) => option.value === filters.readState)?.label,
    ].join(' · ');
  }, [filters, subscriptions, subscriptionOptions]);

  const selectedConversation = useMemo(() => {
    if (!selectedTarget) return null;
    const key = getConversationKey(selectedTarget.subscriptionId, selectedTarget.phone);
    return (
      displayedConversations.find((conversation) => conversation.key === key) ??
      buildConversationDisplay(
        buildEmptyConversation(selectedTarget.subscriptionId, selectedTarget.phone),
        contactsById,
        tagsById,
        subscriptions,
        userNameBySubscription,
      )
    );
  }, [selectedTarget, displayedConversations, contactsById, tagsById, subscriptions, userNameBySubscription]);

  const setUnreadCount = (key: string, unreadCount: number) =>
    setConversations((previous) =>
      previous.map((conversation) =>
        getConversationKey(conversation.subscription_id, conversation.phone) === key
          ? { ...conversation, unread_count: unreadCount }
          : conversation,
      ),
    );

  // Optimistic: the badge changes at once and comes back if the call fails
  const setRead = async (conversation: ConversationDisplay, read: boolean) => {
    if (!companyId || !authentication) return;

    const previousUnreadCount = conversation.unreadCount;
    setUnreadCount(conversation.key, read ? 0 : Math.max(previousUnreadCount, 1));
    try {
      await markConversationRead(
        { company_id: companyId, subscription_id: conversation.subscriptionId, phone: conversation.phone, read },
        authentication,
      );
    } catch (e) {
      setUnreadCount(conversation.key, previousUnreadCount);
      message.error(e instanceof Error ? e.message : 'Erreur lors de la mise à jour de la conversation');
    }
  };

  const selectConversation = (conversation: ConversationDisplay) => {
    setSelectedTarget({ subscriptionId: conversation.subscriptionId, phone: conversation.phone });
    // Opening a conversation of the user's own line reads it, like on mobile
    if (conversation.isOwn && conversation.unreadCount > 0) setRead(conversation, true);
  };

  return {
    state: {
      conversations: filteredConversations,
      totalCount: filteredConversations.length,
      unreadByTab,
      selectedConversation,
      isLoading,
      isLoadingMore,
      hasMore: pendingCursors !== null && loadMoreFailedFilters !== filters,
      filters,
      // A member reads only their own line: no scope to pick
      canSelectSubscription: subscriptions.length > 1,
      subscriptionOptions,
      activeFiltersCount: countActiveFilters(filters),
      filtersSummary,
    },
    actions: {
      setTab: updateFilter('tab'),
      setSearch: updateFilter('search'),
      setSubscriptionId: (subscriptionId: string | null) => updateFilter('subscriptionId')(subscriptionId || null),
      setSortBy: updateFilter('sortBy'),
      setReadState: updateFilter('readState'),
      setPeriod: updateFilter('period'),
      // Tab, search and sort are not in the panel: they stay
      resetFilters: () =>
        setFilters((previous) => ({
          ...previous,
          subscriptionId: DEFAULT_CONVERSATIONS_FILTERS.subscriptionId,
          readState: DEFAULT_CONVERSATIONS_FILTERS.readState,
          period: DEFAULT_CONVERSATIONS_FILTERS.period,
        })),
      selectConversation,
      setRead,
      loadMore,
      refresh: fetchConversations,
    },
  };
};

export default useConversationsPageController;
